import React, { memo, useCallback, useMemo } from 'react';
import { Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { AddIcon } from '@/assets/icons';
import { TextInput } from '@/modules/common/components/inputs';

type Props = {
  disabled?: boolean;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  canAdd: boolean;
  showDuplicateError: boolean;
  invalidErrorText?: string;
  onAdd: () => void | Promise<void>;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
};

function AddRowComponent({
  disabled,
  value,
  onChange,
  placeholder,
  canAdd,
  showDuplicateError,
  invalidErrorText,
  onAdd,
  onInputFocus,
  onInputBlur,
}: Props) {
  const { t } = useTranslation('interface');

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // handle enter key to add option
      if (e.key !== 'Enter') return;
      e.preventDefault();
      e.stopPropagation();
      onAdd();
    },
    [onAdd],
  );

  const helperText = useMemo(() => {
    if (disabled) return undefined;
    if (showDuplicateError) return t('select_options_editor.duplicate', 'Duplicate');
    if (invalidErrorText) return invalidErrorText;
    return undefined;
  }, [disabled, showDuplicateError, invalidErrorText, t]);

  return (
    <Stack
      direction='row'
      gap={1}
      justifyContent='space-between'
      alignItems='center'
      paddingBottom={2}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <TextInput
          value={value}
          onChange={onChange}
          disabled={disabled}
          sx={{ width: '100%' }}
          placeholder={placeholder}
          error={!disabled && (showDuplicateError || !!invalidErrorText)}
          helperText={helperText}
          enterBlur={false}
          inputProps={{
            inputProps: {
              onKeyDown,
            },
          }}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          errorType='floating'
        />
      </Stack>

      <Button
        size='small'
        sx={{ boxShadow: 'none' }}
        variant='contained'
        startIcon={<AddIcon />}
        disabled={!canAdd}
        onClick={onAdd}
      >
        {t('common:add', 'Add')}
      </Button>
    </Stack>
  );
}

export const AddRow = memo(AddRowComponent);
